/**
 * useCompanyConfiguration
 *
 * Thin wrapper around `loadCompanyConfiguration` from @puffin-core/subdomain-routing.
 * Loads company info (name, logo, subdomain) when a companyId is present and
 * exposes { company, isLoading, error } for the company banner (`@puffin-core/auth-shell`'s CompanyContextBanner).
 *
 * @module @puffin-core/login-shell/hooks/useCompanyConfiguration
 */

import { useEffect, useState } from 'react';
import { loadCompanyConfiguration } from '@puffin-core/subdomain-routing';

/**
 * @param {string|null|undefined} companyId
 * @returns {{ company: {name?:string, logo:?string, subdomain?:string}|null, isLoading: boolean, error: Error|null }}
 */
export function useCompanyConfiguration(companyId) {
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (!companyId) {
      setCompany(null);
      setIsLoading(false);
      setError(null);
      return () => {
        isMounted = false;
      };
    }
    setIsLoading(true);
    setError(null);
    loadCompanyConfiguration(companyId)
      .then((config) => {
        if (!isMounted) return;
        if (config) {
          setCompany({
            name: config.name,
            // The resolve endpoint's own answer (TENANT-LOGO): an absolute media-library address, or null.
            logo: config.logo ?? null,
            subdomain: config.subdomain,
          });
        } else {
          setCompany(null);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setCompany(null);
        setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [companyId]);

  return { company, isLoading, error };
}

export default useCompanyConfiguration;
